import styled, { css } from 'styled-components';
import { up } from 'styled-breakpoints';

export const Container = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border: 1px solid var(--colors-background-40);
    border-radius: 0.75rem;
    overflow: hidden;
    margin-bottom: 5rem;

    ${up('lg')} {
      border-radius: 0.75rem;
    }
  `}
`;

export const TabsList = styled.div`
  ${() => css`
    display: flex;
    gap: 0.5rem;
    padding: 0 1rem;
    border-bottom: 1px solid var(--colors-background-40);
    overflow-x: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  `}
`;

type TabProps = {
  $active?: boolean;
};

export const Tab = styled.button<TabProps>`
  ${({ theme, $active }) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[60]};
    background: transparent;
    border: none;
    border-bottom: 2px solid
      ${$active ? theme.colors.brand.primary.default : 'transparent'};
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    margin-bottom: -1px;

    svg {
      width: 1.125rem;
      height: 1.125rem;
    }

    &:hover {
      color: ${theme.colors.brand.primary.default};
    }
  `}
`;

export const TabBadge = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${theme.colors.brand.primary.default};
    background: ${theme.colors.brand.primary.opaque};
    border-radius: 0.625rem;
  `}
`;

export const TabContent = styled.div`
  padding: 1rem;

  ${up('lg')} {
    padding: 1.25rem;
  }
`;

export const ContentText = styled.div`
  ${({ theme }) => css`
    font-size: 0.875rem;
    color: ${theme.colors.foreground[80]};
    line-height: 1.6;
    word-break: break-word;

    p {
      margin: 0 0 1rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: ${theme.colors.foreground[100]};
      margin: 0 0 0.75rem;
      line-height: 1.3;
    }

    h1 {
      font-size: 1.5rem;
    }
    h2 {
      font-size: 1.25rem;
    }
    h3 {
      font-size: 1.125rem;
    }
    h4 {
      font-size: 1rem;
    }

    ul,
    ol {
      margin: 0 0 1rem;
      padding-left: 1.5rem;
    }

    li {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    a {
      color: ${theme.colors.brand.primary.default};
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }

    strong,
    b {
      font-weight: 600;
      color: ${theme.colors.foreground[100]};
    }

    em,
    i {
      font-style: italic;
    }

    blockquote {
      margin: 0 0 1rem;
      padding: 0.75rem 1rem;
      border-left: 3px solid ${theme.colors.brand.primary.default};
      background: ${theme.colors.background[80]};
      border-radius: 0 0.25rem 0.25rem 0;
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 0.5rem;
    }

    hr {
      border: none;
      border-top: 1px solid var(--colors-background-40);
      margin: 1rem 0;
    }
  `}
`;

export const EmptyState = styled.p`
  ${({ theme }) => css`
    font-size: 0.875rem;
    color: ${theme.colors.foreground[60]};
    text-align: center;
    padding: 2rem 1rem;
  `}
`;

// Speakers styles
export const SpeakersList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SpeakerItem = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 0;
    border-bottom: 1px solid ${theme.colors.background[80]};
    cursor: pointer;
    transition: background 0.2s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: ${theme.colors.background[80]};
      margin: 0 -1rem;
      padding: 1rem;
      border-radius: 0.5rem;
    }
  `}
`;

export const SpeakerAvatar = styled.div`
  ${({ theme }) => css`
    width: 3rem;
    height: 3rem;
    background: ${theme.colors.brand.primary.opaque};
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 700;
    color: ${theme.colors.brand.primary.default};
    flex-shrink: 0;
  `}
`;

export const SpeakerPhoto = styled.img`
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  object-fit: cover;
  flex-shrink: 0;
`;

export const SpeakerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

export const SpeakerName = styled.span`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const SpeakerRole = styled.span`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[60]};
  `}
`;

export const SpeakerChevron = styled.span`
  ${({ theme }) => css`
    color: ${theme.colors.foreground[40]};
    flex-shrink: 0;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  `}
`;

// Schedule styles
export const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ScheduleItem = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[80]};
    border-left: 3px solid ${theme.colors.brand.primary.default};
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border-color: ${theme.colors.brand.primary.default};
    }
  `}
`;

export const ScheduleDateColumn = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 3.5rem;
    padding-right: 1rem;
    border-right: 1px solid ${theme.colors.background[80]};
  `}
`;

export const ScheduleDayName = styled.span`
  ${({ theme }) => css`
    font-size: 0.625rem;
    font-weight: 600;
    color: ${theme.colors.foreground[60]};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  `}
`;

export const ScheduleDayNumber = styled.span`
  ${({ theme }) => css`
    font-size: 1.75rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
    line-height: 1;
  `}
`;

export const ScheduleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;

export const ScheduleTitle = styled.h4`
  ${({ theme }) => css`
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    line-height: 1.3;
  `}
`;

export const ScheduleDescription = styled.p`
  ${({ theme }) => css`
    margin: 0;
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[60]};
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

export const ScheduleFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.25rem;
`;

export const ScheduleTime = styled.span`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[60]};

    svg {
      width: 1rem;
      height: 1rem;
    }
  `}
`;

export const ScheduleReminderButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${theme.colors.brand.primary.default};
    background: transparent;
    border: 1px solid ${theme.colors.brand.primary.default};
    border-radius: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
      width: 0.875rem;
      height: 0.875rem;
    }

    &:hover {
      background: ${theme.colors.brand.primary.opaque};
    }
  `}
`;

export const ScheduleChevron = styled.span`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.foreground[40]};
    flex-shrink: 0;
    margin-left: auto;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  `}
`;

// Files styles
export const FilesList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FileItem = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 0;
    border-bottom: 1px solid ${theme.colors.background[80]};

    &:last-child {
      border-bottom: none;
    }
  `}
`;

type FileIconWrapperProps = {
  $type: 'doc' | 'pdf' | 'image' | 'default';
};

export const FileIconWrapper = styled.div<FileIconWrapperProps>`
  ${({ theme }) => css`
    width: 3rem;
    height: 3rem;
    background: ${theme.colors.brand.primary.opaque};
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 1.5rem;
      height: 1.5rem;
      color: ${theme.colors.brand.primary.default};
    }
  `}
`;

export const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.span`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 500;
    color: ${theme.colors.foreground[100]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const FileMeta = styled.span`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[60]};
    text-transform: uppercase;
  `}
`;

export const FileDownloadButton = styled.a`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background: ${theme.colors.background[80]};
    border-radius: 0.5rem;
    color: ${theme.colors.foreground[60]};
    transition: all 0.2s ease;
    flex-shrink: 0;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    &:hover {
      background: ${theme.colors.brand.primary.opaque};
      color: ${theme.colors.brand.primary.default};
    }
  `}
`;
